import {
  type MutationOptions,
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';
import { getClassesApiQueryKey } from 'eds-common/api/v3';
import { type GoogleCourse } from '../use-get-google-courses-api/use-get-google-courses-api';
import { fetcher } from '../../../utils/api/fetcher/fetcher';

// A co-teacher cannot import a Google class until its owner imports it.
export const GOOGLE_CLASS_NOT_IMPORTED_ERROR_CODE = 'GOOGLE_CLASS_NOT_IMPORTED';

export const errorDetailSchema = z.object({
  code: z.string().optional(),
  message: z.string().optional(),
});

export const classSyncSuccessSchema = z.object({
  className: z.string(),
  sourceId: z.string().optional(),
  studentsAddedCount: z.number().optional(),
  studentsRemovedCount: z.number().optional(),
});

export const classSyncFailureSchema = z.object({
  className: z.string(),
  sourceId: z.string().optional(),
  errorDetail: errorDetailSchema.optional(),
});

export const studentSyncFailureSchema = z.object({
  studentName: z.string(),
  className: z.string(),
  sourceId: z.string().optional(),
  errorDetail: errorDetailSchema.optional(),
});

export const syncSuccessSummarySchema = z.object({
  classesSynced: z.array(classSyncSuccessSchema).optional(),
});

export const syncFailureSummarySchema = z.object({
  classErrors: z.array(classSyncFailureSchema).optional(),
  studentErrors: z.array(studentSyncFailureSchema).optional(),
});

export const googleClassesResponseBaseSchema = z.object({
  errors: syncFailureSummarySchema.nullish(),
});

const importGoogleClassesApiResponseSchema =
  googleClassesResponseBaseSchema.extend({
    success: syncSuccessSummarySchema.nullish(),
  });

interface GoogleImportClassesApiRequestBody {
  classes: Array<{
    source_id: string;
    name: string;
  }>;
}

type GroupedStudentSyncFailure = {
  className: string;
  students: ImportGoogleClassesStudentSyncFailure[];
};

export type ImportGoogleClassesApiResponse = z.infer<
  typeof importGoogleClassesApiResponseSchema
>;
export type ImportGoogleClassesClassSyncSuccess = z.infer<
  typeof classSyncSuccessSchema
>;
export type ImportGoogleClassesClassSyncFailure = z.infer<
  typeof classSyncFailureSchema
>;
export type ImportGoogleClassesStudentSyncFailure = z.infer<
  typeof studentSyncFailureSchema
>;

export type GoogleClassesSuccessToastVariant = 'import' | 'sync-multiple';

function toImportRequestBody(
  classes: GoogleCourse[]
): GoogleImportClassesApiRequestBody {
  return {
    classes: classes.map((c) => ({
      source_id: c.course_id,
      name: c.course_name,
    })),
  };
}

export async function importGoogleClassesApi(
  classes: GoogleCourse[]
): Promise<ImportGoogleClassesApiResponse> {
  const response = await fetcher<ImportGoogleClassesApiResponse>(
    '/google/import',
    {
      method: 'POST',
      body: JSON.stringify(toImportRequestBody(classes)),
      service: '/rosteradapter',
      version: '/v2',
    }
  );

  return importGoogleClassesApiResponseSchema.parse(response);
}

export function hasImportGoogleClassesIssues(
  response: ImportGoogleClassesApiResponse
) {
  return Boolean(
    response.errors?.classErrors?.length ||
      response.errors?.studentErrors?.length
  );
}

export function groupImportGoogleClassesStudentErrors(
  studentErrors: ImportGoogleClassesStudentSyncFailure[] = []
): GroupedStudentSyncFailure[] {
  const groupedStudentErrors = new Map<string, GroupedStudentSyncFailure>();

  studentErrors.forEach((studentError) => {
    const existingGroup = groupedStudentErrors.get(studentError.className);

    if (existingGroup) {
      existingGroup.students.push(studentError);
      return;
    }

    groupedStudentErrors.set(studentError.className, {
      className: studentError.className,
      students: [studentError],
    });
  });

  return Array.from(groupedStudentErrors.values());
}

export function getGoogleClassesSuccessToastMessage(
  variant: GoogleClassesSuccessToastVariant,
  classCount = 1
) {
  if (variant === 'import') {
    const classLabel = classCount === 1 ? 'class' : 'classes';
    return `${classCount} ${classLabel} imported.`;
  }

  const classLabel = classCount === 1 ? 'Class' : 'Classes';
  return `${classLabel} synced successfully.`;
}

export function getImportGoogleClassesSuccessToastMessage(
  response: ImportGoogleClassesApiResponse
) {
  return getGoogleClassesSuccessToastMessage(
    'import',
    response.success?.classesSynced?.length ?? 0
  );
}

export function useImportGoogleClassesApi(
  customMutationOptions?: Omit<
    MutationOptions<ImportGoogleClassesApiResponse, Error, GoogleCourse[]>,
    'mutationFn'
  >
): UseMutationResult<ImportGoogleClassesApiResponse, Error, GoogleCourse[]> {
  const queryClient = useQueryClient();
  const { onSuccess, ...opts } = customMutationOptions ?? {};

  return useMutation({
    mutationFn: (classes) => importGoogleClassesApi(classes),
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: getClassesApiQueryKey(),
      });

      await onSuccess?.(data, variables, context);
    },
    ...opts,
  });
}

export type { GroupedStudentSyncFailure };
